/**
 * Text normalization for facility-locator service-type matching.
 *
 * Both the user's typed term and the strings we match against (service names,
 * AKA, descriptions, common conditions, plural equivalents, misspellings,
 * synonyms) are passed through `normalizeSearchTerm` so that comparisons are
 * insensitive to case, surrounding whitespace, internal whitespace runs,
 * hyphen vs. space, and punctuation.
 *
 * `singularize` / `singularizePhrase` provide a lightweight English plural
 * heuristic for cases the curated `pluralEquivalents` / `synonyms` arrays
 * don't cover (e.g. user types "therapies" but the data only carries
 * "therapy"). Irregular plurals like "teeth → tooth" are intentionally not
 * handled here — those live in the curated synonym map.
 */

const DASH_CHARS = /[‐-―−-]+/g;
// Apostrophes bind a word together ("don't" -> "dont", not "don t"),
// so strip them entirely rather than converting them to whitespace.
const APOSTROPHES = /['‘’`]/g;
// Strip anything that isn't a letter, number, whitespace, or `+`
// (`+` is kept so terms like "LGBTQ+" survive normalization).
const NON_WORD_CHARS = /[^\p{L}\p{N}+\s]/gu;
const WHITESPACE_RUN = /\s+/g;

export const normalizeSearchTerm = input => {
  if (input == null) return '';
  return String(input)
    .toLowerCase()
    .replace(APOSTROPHES, '')
    .replace(DASH_CHARS, ' ')
    .replace(NON_WORD_CHARS, ' ')
    .replace(WHITESPACE_RUN, ' ')
    .trim();
};

// Words that look plural (end in "es"/"as") but are grammatically invariant or
// singular, and that the heuristics below don't otherwise protect. Stripping
// their trailing "s" (or applying the "ies -> y" rule) would corrupt them
// ("diabetes" -> "diabete", "rabies" -> "raby") and broaden matching, so they
// are left untouched. Latin/Greek singulars ending in "-is" / "-us"
// (arthritis, hepatitis, campus, lupus, "-ous" adjectives, ...) are handled
// generically by the trailing-"s" guard and don't need to be listed here.
// Compared case-insensitively against the already-lowercased input.
const INVARIANT_TERMS = new Set([
  'diabetes',
  'herpes',
  'measles',
  'rabies',
  'scabies',
  'shingles',
  'mumps',
  'series',
  'species',
  'pancreas',
  'overseas',
]);

export const singularize = word => {
  if (!word || word.length < 4) return word;
  // Leave invariant medical/general terms alone (e.g. "diabetes").
  if (INVARIANT_TERMS.has(word.toLowerCase())) return word;
  // therapies -> therapy (but not "ties" or "lies")
  if (/[^aeiou]ies$/i.test(word)) return word.replace(/ies$/i, 'y');
  // matches -> match, classes -> class, boxes -> box, dishes -> dish
  if (/(sses|xes|zes|ches|shes)$/i.test(word)) {
    return word.replace(/es$/i, '');
  }
  // dentists -> dentist. Don't strip after "s" (class), "i" (arthritis,
  // diagnosis, crisis) or "u" (campus, lupus, nervous) — those signal an
  // "-ss" word or a Latin/Greek singular, not an English plural.
  if (/[^siu]s$/i.test(word)) return word.slice(0, -1);
  return word;
};

export const singularizePhrase = phrase =>
  phrase.split(' ').map(singularize).join(' ');
